import { useSnackbar } from 'notistack';
import { ActivationErrorKey, BulkActivationResultItemDTO, BulkActivationResultItemDTOWarningEnum } from 'probonio-shared-ui/api';

type EnqueueSnackbar = ReturnType<typeof useSnackbar>['enqueueSnackbar'];
type TranslateFn = (key: string | string[], options?: Record<string, unknown>) => string;

export interface BulkResultMessageKeys {
  empty: string;
  successFragment: string;
}

/**
 * Shows a single combined snackbar for a bulk activation result
 */
export function enqueueBulkResultSnackbar(
  results: BulkActivationResultItemDTO[],
  t: TranslateFn,
  enqueueSnackbar: EnqueueSnackbar,
  keys: BulkResultMessageKeys,
): number {
  const successResults = results.filter(result => result.status === 'SUCCESS');
  const adjustedResults = results.filter(result => result.status === 'ADJUSTMENT');
  const blockedResults = results.filter(result => result.status === 'BLOCKED');
  const errorResults = results.filter(result => result.status === 'ERROR');
  const successCount = successResults.length;
  // ADJUSTMENT = applied but with a modified end date; BLOCKED = not applied at all
  const adjustedCount = adjustedResults.length;
  const blockedCount = blockedResults.length;
  const errorCount = errorResults.length;
  // a capped result is a SUCCESS with an adjusted endDate (status 'SUCCESS' + warning), so it is a subset of successes
  const cappedCount = successResults.filter(
    result => result.warning === BulkActivationResultItemDTOWarningEnum.TerminationDateCapped,
  ).length;

  if (results.length === 0) {
    enqueueSnackbar(t(keys.empty), { variant: 'warning' });
    return 0;
  }

  // total header + one bullet line per outcome. skipped items are grouped by their reason key so the
  // manager sees why (e.g. missing credit card); the success line additionally notes endDate capping
  const lines: string[] = [t('bulkResult.processed', { count: results.length })];
  if (successCount) {
    const capped = cappedCount ? ` (${t('bulkResult.cappedSuffix', { count: cappedCount })})` : '';
    lines.push(`• ${t(keys.successFragment, { count: successCount })}${capped}`);
  }
  if (adjustedCount) {
    lines.push(`• ${t('bulkResult.adjusted', { count: adjustedCount })}`);
  }
  if (blockedCount) {
    lines.push(`• ${t('bulkResult.blocked', { count: blockedCount })}`);
  }
  if (errorCount) {
    // errors carrying a reason key are grouped by it, the rest is summed up as a generic "Übersprungen"
    const countByReason = new Map<ActivationErrorKey, number>();
    for (const result of errorResults) {
      if (result.errorKey) {
        countByReason.set(result.errorKey, (countByReason.get(result.errorKey) ?? 0) + 1);
      }
    }
    let labeledCount = 0;
    for (const [errorKey, count] of countByReason) {
      // prefer a bulk-specific wording, fall back to the generic single-activation error text; a key
      // the frontend does not know yet (API is ahead) has no text at all and stays unlabeled
      const reason = t([`bulkResult.${errorKey}`, `activationError.${errorKey}`], { defaultValue: '' });
      if (reason) {
        lines.push(`• ${t('bulkResult.skippedReason', { count, reason })}`);
        labeledCount += count;
      }
    }
    const unlabeledCount = errorCount - labeledCount;
    if (unlabeledCount) {
      lines.push(`• ${t('bulkResult.skipped', { count: unlabeledCount })}`);
    }
  }

  // a change was applied (fully or adjusted); ERROR/BLOCKED mean nothing was applied for that employee
  const appliedCount = successCount + adjustedCount;
  const notAppliedCount = errorCount + blockedCount;
  // green only when everything applied cleanly; warning when something was applied but adjusted/blocked/failed; error when nothing applied
  const variant = notAppliedCount ? (appliedCount ? 'warning' : 'error') : cappedCount || adjustedCount ? 'warning' : 'success';
  enqueueSnackbar(lines.join('\n'), { variant, style: { whiteSpace: 'pre-line' }, autoHideDuration: 10000 });

  return appliedCount;
}
